<script lang="ts">
  import { pageTitle } from '../../lib/pageTitle';
  import { consentStore } from '../../lib/consent';
  import { publicAppOrigin } from '#lib/publicUrls.ts';
</script>

<svelte:head>
  <title>{pageTitle('Cookie Policy')}</title>
</svelte:head>

<div class="container clear-nav">
  <h1>Cookie Policy</h1>
  <p><strong>Last updated:</strong> May 7, 2026</p>

  <p>
    This Cookie Policy explains the cookies and similar technologies used by
    SpinFlip LLC on
    <a href={publicAppOrigin()} target="_blank" rel="noopener noreferrer"
      >{publicAppOrigin()}</a
    > (the "Service"), what they do, and how you can control them. It
    supplements our <a href="/privacy">Privacy Policy</a>.
  </p>

  <h2>What are cookies and similar technologies?</h2>
  <p>
    "Cookies" are small text files that a website places on your device. They
    are typically used to make websites work, to make them work more
    efficiently, and to provide reporting information. We also use related
    browser-storage mechanisms — <code>localStorage</code> and
    <code>sessionStorage</code> — which behave similarly to cookies for the
    purposes of this policy.
  </p>
  <p>
    Cookies are classified as <strong>first-party</strong> (set by us) or
    <strong>third-party</strong> (set by another organization, such as Google).
    They are also classified by purpose: <strong>strictly necessary</strong>
    cookies are required for the Service to work; <strong>analytics</strong>
    cookies help us understand how the Service is used.
  </p>

  <h2>Your choices</h2>
  <p>
    On your first visit you will see a cookie banner that lets you
    <strong>Accept all</strong>, <strong>Reject all</strong>, or
    <strong>Manage preferences</strong>. You can change your choice at any time
    by reopening the banner:
  </p>
  <p>
    <button
      type="button"
      class="manage-btn"
      on:click={() => consentStore.openPreferences()}
    >
      Open Cookie Preferences
    </button>
  </p>
  <p>
    We honor the
    <a href="https://globalprivacycontrol.org/" target="_blank" rel="noopener noreferrer">Global Privacy Control (GPC)</a>
    browser signal: when GPC is enabled in your browser, we automatically opt
    you out of all non-essential cookies and treat the signal as a request to
    opt out of any "sale" or "sharing" of personal information under U.S.
    state privacy laws.
  </p>
  <p>
    You can also block or delete cookies through your browser settings.
    Disabling strictly necessary cookies will break key features of the
    Service (for example, you will not be able to keep a shopping cart or stay
    logged in).
  </p>

  <h2>Cookies and storage we use</h2>

  <h3>Strictly necessary</h3>
  <p>
    These cannot be disabled because the Service cannot function without them.
    No consent is required.
  </p>
  <ul>
    <li>
      <strong>Name:</strong> <code>spinflip-store-cart</code><br />
      <strong>Provider:</strong> spin-flip.com (first-party)<br />
      <strong>Type:</strong> <code>localStorage</code><br />
      <strong>Purpose:</strong> Remembers items in your shopping cart between
      visits.<br />
      <strong>Expires:</strong> Until you clear your browser storage or empty
      your cart.
    </li>
    <li>
      <strong>Name:</strong> <code>token</code><br />
      <strong>Provider:</strong> spin-flip.com (first-party)<br />
      <strong>Type:</strong> <code>localStorage</code><br />
      <strong>Purpose:</strong> Stores your authentication token after sign-in
      so authenticated requests can be made on your behalf.<br />
      <strong>Expires:</strong> When you sign out or clear your browser
      storage.
    </li>
    <li>
      <strong>Name:</strong> <code>spinflip-consent-v1</code><br />
      <strong>Provider:</strong> spin-flip.com (first-party)<br />
      <strong>Type:</strong> <code>localStorage</code><br />
      <strong>Purpose:</strong> Records your cookie preferences so we don't
      ask again on every visit.<br />
      <strong>Expires:</strong> 12 months from the date you set your
      preference.
    </li>
    <li>
      <strong>Name:</strong> <code>spinflip_admin_key</code><br />
      <strong>Provider:</strong> spin-flip.com (first-party)<br />
      <strong>Type:</strong> <code>sessionStorage</code><br />
      <strong>Purpose:</strong> Holds the administrator API key during an
      admin session. Set only on <code>/admin</code> after admin login.<br />
      <strong>Expires:</strong> When you close the browser tab.
    </li>
  </ul>

  <h3>Analytics (optional)</h3>
  <p>
    Loaded only after you grant consent to the Analytics category. Used to
    understand how visitors interact with the Service in aggregate.
  </p>
  <ul>
    <li>
      <strong>Name:</strong> <code>_ga</code><br />
      <strong>Provider:</strong> Google LLC (third-party,
      <code>googletagmanager.com</code> / <code>google-analytics.com</code>)<br />
      <strong>Type:</strong> HTTP cookie<br />
      <strong>Purpose:</strong> Distinguishes unique visitors for Google
      Analytics 4.<br />
      <strong>Expires:</strong> 2 years from set or last update.
    </li>
    <li>
      <strong>Name:</strong> <code>_ga_C317H4J50S</code><br />
      <strong>Provider:</strong> Google LLC (third-party)<br />
      <strong>Type:</strong> HTTP cookie<br />
      <strong>Purpose:</strong> Persists session state for Google Analytics 4
      property <code>G-C317H4J50S</code>.<br />
      <strong>Expires:</strong> 2 years from set or last update.
    </li>
  </ul>

  <h3>Cookies set by other sites you may interact with</h3>
  <p>
    The following are not set by spin-flip.com but may be set by other
    websites you reach through the Service. They are governed by the
    operator's own cookie policy.
  </p>
  <ul>
    <li>
      <strong>Stripe Checkout</strong> — When you proceed to checkout, you are
      redirected to <code>checkout.stripe.com</code>, which sets its own
      cookies for fraud prevention and to maintain your checkout session. See
      <a href="https://stripe.com/cookies-policy/legal" target="_blank" rel="noopener noreferrer">Stripe's Cookie Policy</a>.
    </li>
    <li>
      <strong>Embedded fonts and assets</strong> — Requests to Google Fonts
      (<code>fonts.googleapis.com</code>, <code>fonts.gstatic.com</code>),
      jsDelivr (<code>cdn.jsdelivr.net</code>), and Cloudflare CDN
      (<code>cdnjs.cloudflare.com</code>) reveal your IP address and browser
      headers to those providers but do not set cookies in your browser for
      our origin.
    </li>
  </ul>

  <h2>Changes to this policy</h2>
  <p>
    If we add, remove, or change the purpose of cookies in a material way, we
    will update this page and re-prompt you for consent through the cookie
    banner.
  </p>

  <h2>Contact</h2>
  <p>
    Questions about cookies on this Service? Email
    <a href="mailto:support@spin-flip.com">support@spin-flip.com</a>.
  </p>
</div>

<style>
  .clear-nav {
    margin-top: 0;
    font-size: 1rem;
    line-height: 1.6;
    padding: 0 1rem;
  }

  ul {
    margin-left: 1.5rem;
  }

  ul li {
    margin-bottom: 1rem;
  }

  h2 {
    margin-top: 2rem;
  }

  h3 {
    margin-top: 1.5rem;
  }

  code {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0.1em 0.3em;
    border-radius: 3px;
    font-size: 0.9em;
  }

  .manage-btn {
    background-color: #fed136;
    color: #212529;
    border: 1px solid #fed136;
    border-radius: 4px;
    padding: 0.5rem 1rem;
    font-weight: 600;
    cursor: pointer;
  }

  .manage-btn:hover,
  .manage-btn:focus {
    background-color: #e6bd2e;
  }

  @media (max-width: 768px) {
    .clear-nav {
      font-size: 0.9rem;
    }

    ul li {
      margin-bottom: 0.8rem;
    }
  }
</style>
